import { createFileRoute, notFound } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Award, Zap } from "lucide-react";
import { getPublicPortfolio } from "@/lib/api/lxp-portfolio.functions";

const qo = (userId: string) =>
  queryOptions({ queryKey: ["lxp", "portfolio", userId], queryFn: () => getPublicPortfolio({ data: { userId } }) });

export const Route = createFileRoute("/lexpractiq/portfolio/$userId")({
  loader: async ({ context, params }) => {
    const d = await context.queryClient.ensureQueryData(qo(params.userId));
    if (!d.profile) throw notFound();
    return d;
  },
  head: ({ loaderData }) => {
    const p = loaderData?.profile;
    if (!p) return { meta: [{ title: "Portfolio | SA LexPractiq" }, { name: "robots", content: "noindex" }] };
    return {
      meta: [
        { title: `${p.display_name} — Portfolio | SA LexPractiq` },
        { name: "description", content: p.headline || `Legal experience portfolio for ${p.display_name}` },
        { property: "og:title", content: `${p.display_name} — SA LexPractiq portfolio` },
        { property: "og:description", content: p.headline || `Verified legal work by ${p.display_name}` },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="lxp-container" style={{ padding: "5rem 1.25rem", textAlign: "center" }}>
      <h1>Portfolio not public</h1>
      <p className="lxp-muted">This portfolio is private or has not been published yet.</p>
    </div>
  ),
  component: PortfolioPage,
});

function PortfolioPage() {
  const { userId } = Route.useParams();
  const { data } = useSuspenseQuery(qo(userId));
  const p = data.profile!;
  return (
    <div className="lxp-container" style={{ padding: "3rem 1.25rem", maxWidth: 960 }}>
      <div className="lxp-card" style={{ padding: "2rem" }}>
        <h1 style={{ margin: "0 0 0.5rem" }}>{p.display_name}</h1>
        {p.headline && <p className="lxp-muted" style={{ margin: "0 0 0.5rem" }}>{p.headline}</p>}
        {p.bio && <p style={{ margin: 0, lineHeight: 1.6, fontSize: "0.95rem" }}>{p.bio}</p>}
      </div>

      <h2 style={{ fontSize: "1.2rem", margin: "2rem 0 1rem" }}>
        <Zap size={18} style={{ verticalAlign: "-2px", marginRight: 6, color: "var(--lxp-accent)" }} />
        Skills profile
      </h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.75rem" }}>
        {data.competencies.map((c) => (
          <div key={c.competency_key} className="lxp-card">
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
              <strong style={{ fontSize: "0.9rem" }}>{c.competency_label}</strong>
              <span className="lxp-badge">Level {c.level}</span>
            </div>
            <div style={{ height: 6, background: "var(--lxp-surface)", borderRadius: 999 }}>
              <div style={{
                height: "100%", width: `${Math.min(100, (c.xp / (c.level * 500)) * 100)}%`,
                background: "linear-gradient(90deg, var(--lxp-accent), #38bdf8)", borderRadius: 999,
              }} />
            </div>
            <div className="lxp-muted" style={{ fontSize: "0.75rem", marginTop: "0.35rem" }}>{c.xp} XP</div>
          </div>
        ))}
        {data.competencies.length === 0 && <p className="lxp-muted">No skills tracked yet.</p>}
      </div>

      <h2 style={{ fontSize: "1.2rem", margin: "2rem 0 1rem" }}>
        <Award size={18} style={{ verticalAlign: "-2px", marginRight: 6, color: "var(--lxp-accent)" }} />
        Certificates
      </h2>
      <div style={{ display: "grid", gap: "0.75rem" }}>
        {data.certificates.map((c: any) => (
          <div key={c.certificate_number} className="lxp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <strong>{c.simulation_title}</strong>
              <div className="lxp-muted" style={{ fontSize: "0.8rem" }}>
                {c.certificate_number} · Issued {new Date(c.issued_at).toLocaleDateString()}
              </div>
            </div>
            <a href={`/lexpractiq/verify/${c.verification_token}`} className="lxp-btn-ghost" style={{ fontSize: "0.8rem" }}>Verify</a>
          </div>
        ))}
        {data.certificates.length === 0 && <p className="lxp-muted">No certificates yet.</p>}
      </div>

      <h2 style={{ fontSize: "1.2rem", margin: "2rem 0 1rem" }}>Work samples</h2>
      <div style={{ display: "grid", gap: "0.75rem" }}>
        {data.samples.map((s) => (
          <div key={s.id} className="lxp-card">
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
              <strong>{s.task_title}</strong>
              <span className="lxp-badge">{s.simulation_title}</span>
            </div>
            <div style={{ fontSize: "0.9rem", lineHeight: 1.6, whiteSpace: "pre-wrap", color: "var(--lxp-text-muted)" }}>
              {s.body_html.slice(0, 800)}{s.body_html.length > 800 ? "…" : ""}
            </div>
          </div>
        ))}
        {data.samples.length === 0 && <p className="lxp-muted">No public work samples yet.</p>}
      </div>
    </div>
  );
}
